import { useState } from "react";
import axios from "axios";
import { Search, Check, Loader2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { useLanguage } from "@/i18n";
import { API, TRACK_STRINGS } from "@/utils";

const STEPS = ["new", "contacted", "scheduled", "completed"];

export default function TrackPage() {
  const { lang } = useLanguage();
  const ts = TRACK_STRINGS[lang] || TRACK_STRINGS.en;
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    setNotFound(false);
    setResult(null);
    try {
      const { data } = await axios.get(`${API}/leads/track/${code.trim().toLowerCase()}`);
      setResult(data);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const currentStep = result ? STEPS.indexOf(result.status) : -1;

  return (
    <div className="App bg-[#FAF9F6] text-[#111111] min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-40 pb-24 px-5" data-testid="track-page">
        <div className="max-w-xl mx-auto">
          <div className="flex items-center gap-3 mb-6 justify-center">
            <span className="h-px w-10 bg-[#B8860B]" />
            <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#B8860B]">ClearRoute</span>
            <span className="h-px w-10 bg-[#B8860B]" />
          </div>
          <h1 className="font-display font-bold tracking-tight text-4xl sm:text-5xl text-center">{ts.title}</h1>
          <p className="mt-4 text-black/55 text-center">{ts.sub}</p>

          <form onSubmit={search} className="mt-10 flex gap-3">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={ts.placeholder}
              data-testid="track-code-input"
              className="flex-1 bg-white border border-black/10 rounded-full px-6 py-4 text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="track-submit-button"
              className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#E6C965] disabled:opacity-60 text-black font-bold px-7 py-4 rounded-full transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {ts.button}
            </button>
          </form>

          {notFound && (
            <p className="mt-6 text-center text-red-600 text-sm font-medium" data-testid="track-not-found">{ts.notFound}</p>
          )}

          {result && (
            <div className="mt-12 bg-white border border-black/10 rounded-3xl p-8 shadow-xl" data-testid="track-result">
              <div className="space-y-6">
                {STEPS.map((s, i) => {
                  const done = i <= currentStep;
                  return (
                    <div key={s} className="flex items-center gap-4" data-testid={`track-step-${s}`}>
                      <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 border-2 transition-colors ${
                        done ? "bg-[#D4AF37] border-[#D4AF37] text-black" : "border-black/15 text-black/30"
                      }`}>
                        <Check className="w-4 h-4" />
                      </span>
                      <div>
                        <div className={`font-display font-semibold ${done ? "text-[#111111]" : "text-black/35"}`}>{ts.statuses[s]}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-8 pt-6 border-t border-black/10 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="block text-[10px] tracking-widest uppercase text-black/40">{ts.serviceLabel}</span>
                  <span className="font-semibold capitalize">{result.service || "—"}</span>
                </div>
                <div>
                  <span className="block text-[10px] tracking-widest uppercase text-black/40">{ts.slotLabel}</span>
                  <span className="font-semibold">{result.slot || "—"}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
